/**
 * Pure, zod-freie Pattern-Lib für Batch-Rename (Library-Bulk + Upload).
 *
 * Client (Live-Vorschau) und Server (Apply) teilen sich `computeNewBase`, damit
 * beide Seiten IDENTISCH rechnen — kein Logik-Duplikat, keine Drift im Kern.
 *
 * ⚠️ Trennung der Zuständigkeit:
 *   - `computeNewBase` erzeugt NUR den Roh-Basenamen (OHNE Extension).
 *   - Sanitize (control chars, slashes, trailing dots, leer-reject) + erzwungene
 *     Extension-Preservation macht server-seitig `applyRename` (lib/filename.ts).
 *   Der Client reattacht für die Vorschau die Extension simpel via `splitExtension`
 *   (kleine Sanitize-Drift bei exotischen Inputs akzeptabel — der Server liefert
 *   nach dem Apply die echten Namen zurück).
 *
 * ⚠️ Dieser File MUSS zod-frei bleiben (client-Bundle), analog asset-constants.ts.
 */

export type RenamePattern =
  | { mode: 'sequence'; base: string; start: number; padding: number }
  | { mode: 'affix'; prefix: string; suffix: string }
  | { mode: 'replace'; search: string; replace: string };

export type RenameMode = RenamePattern['mode'];

/**
 * Trennt Basisname und Extension (ext INKL. führendem Punkt, z.B. ".jpg").
 * Kein Punkt / führender Punkt (dotfile wie ".env") / End-Punkt ("foo.") →
 * ext = '' und base = der ganze String. Spiegelt `extensionOf` in lib/filename.ts.
 */
export function splitExtension(filename: string): { base: string; ext: string } {
  const idx = filename.lastIndexOf('.');
  if (idx <= 0 || idx === filename.length - 1) return { base: filename, ext: '' };
  return { base: filename.slice(0, idx), ext: filename.slice(idx) };
}

/**
 * Erzeugt den neuen Roh-Basenamen (OHNE Extension) für EIN Asset.
 *
 * @param pattern     Muster-Spec (discriminated union)
 * @param currentBase Basisname des Assets OHNE Extension (via splitExtension)
 * @param index       0-basierte Position im Batch (für `sequence`-Nummerierung)
 */
export function computeNewBase(
  pattern: RenamePattern,
  currentBase: string,
  index: number,
): string {
  switch (pattern.mode) {
    case 'sequence': {
      const n = pattern.start + index;
      const num = String(n).padStart(Math.max(1, pattern.padding), '0');
      return `${pattern.base}-${num}`;
    }
    case 'affix':
      return `${pattern.prefix}${currentBase}${pattern.suffix}`;
    case 'replace':
      // Leeres `search` → no-op. (Ohne Guard würde split('') in einzelne Zeichen
      // zerlegen und `replace` zwischen JEDES Zeichen einfügen.)
      if (pattern.search === '') return currentBase;
      return currentBase.split(pattern.search).join(pattern.replace);
  }
}

/**
 * Convenience für die Client-Vorschau: berechnet den voraussichtlichen neuen
 * Dateinamen INKL. Extension. NICHT server-autoritativ (kein voller applyRename-
 * Sanitize) — nur zur Anzeige. Der Server liefert nach dem Apply die echten Namen.
 */
export function previewNewName(
  pattern: RenamePattern,
  currentName: string,
  index: number,
): string {
  const { base, ext } = splitExtension(currentName);
  return `${computeNewBase(pattern, base, index)}${ext}`;
}
